import React, { useState, useCallback, useRef } from 'react';
import { ExternalLink, ChevronLeft, ChevronRight } from 'lucide-react';
import { teamData } from '../data/teamData';
import { partnerData } from '../data/partnerData';
import '../styles/TeamCarousel.css';

// Move TeamSection outside to prevent unnecessary re-renders
const TeamSection = ({ title, data, sectionType, onScroll, isScrolling }) => {
  const handleLeftClick = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    console.log(`Left arrow clicked for ${sectionType}`);
    onScroll(sectionType, 'left');
  }, [sectionType, onScroll]);
  
  const handleRightClick = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    console.log(`Right arrow clicked for ${sectionType}`);
    onScroll(sectionType, 'right');
  }, [sectionType, onScroll]);

  // Duplicate data for infinite scroll
  const infiniteData = [...data, ...data];

  return (
    <div className="team-section">
      <h2 className="section-title">{title}</h2>
      <div className="scrollable-container">
        <button 
          className="scroll-arrow left-arrow"
          onClick={handleLeftClick}
          disabled={isScrolling}
          type="button"
          aria-label={`Scroll ${title} left`}
        >
          <ChevronLeft size={24} />
        </button>
        
        <div className={`team-grid ${sectionType}-grid`}>
          {infiniteData.map((person, index) => (
            <div key={`${index}-${index >= data.length ? 'duplicate' : 'original'}`} className="person-card">
              <div className="person-image-wrapper">
                <img 
                  src={person.image} 
                  alt={person.name}
                  className="person-image"
                />
              </div>
              <div className="person-info">
                <div className="person-header">
                  <a 
                    href={person.linkedIn} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="person-name"
                  >
                    {person.name}
                    <ExternalLink size={16} className="linkedin-icon" />
                  </a>
                  <p className="person-title">{person.title}</p>
                </div>
                <p className="person-bio">{person.bio}</p>
              </div>
            </div>
          ))}
        </div>
        
        <button 
          className="scroll-arrow right-arrow"
          onClick={handleRightClick}
          disabled={isScrolling}
          type="button"
          aria-label={`Scroll ${title} right`}
        >
          <ChevronRight size={24} />
        </button>
      </div>
    </div>
  );
};

const TeamCarousel = () => {
  const [teamScrollPosition, setTeamScrollPosition] = useState(0);
  const [advisorScrollPosition, setAdvisorScrollPosition] = useState(0);
  const [isScrolling, setIsScrolling] = useState(false);
  const scrollingRef = useRef(null);

  // Improved smooth scrolling function using requestAnimationFrame
  const smoothScrollTo = useCallback((element, targetPosition, duration = 600) => {
    if (!element || isScrolling) return Promise.resolve();

    return new Promise((resolve) => {
      setIsScrolling(true);
      const startPosition = element.scrollLeft;
      const distance = targetPosition - startPosition;
      const startTime = performance.now();

      // Easing function for smooth animation
      const easeOutQuart = (t) => 1 - Math.pow(1 - t, 4);

      const animateScroll = (currentTime) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const easedProgress = easeOutQuart(progress);

        element.scrollLeft = startPosition + (distance * easedProgress);

        if (progress < 1) {
          scrollingRef.current = requestAnimationFrame(animateScroll);
        } else {
          setIsScrolling(false);
          scrollingRef.current = null;
          resolve();
        }
      };

      scrollingRef.current = requestAnimationFrame(animateScroll);
    });
  }, [isScrolling]);

  // True infinite scroll with seamless looping
  const scrollSection = async (sectionType, direction) => {
    if (isScrolling) return; // Prevent overlapping scrolls

    const sectionClass = sectionType === 'team' ? '.team-grid' : '.advisors-grid';
    const section = document.querySelector(sectionClass);
    
    if (!section) {
      console.error(`Section ${sectionClass} not found`);
      return;
    }

    // Get responsive card width and gap from CSS
    const computedStyle = window.getComputedStyle(section);
    const cardElement = section.querySelector('.person-card');
    if (!cardElement) {
      console.error('No person card found for measurements');
      return;
    }

    const cardRect = cardElement.getBoundingClientRect();
    const cardWidth = cardRect.width;
    const gap = parseFloat(computedStyle.gap) || 32;
    const scrollAmount = cardWidth + gap;

    const currentScroll = section.scrollLeft;
    const maxScroll = section.scrollWidth - section.clientWidth;
    
    // Get original data length - we have duplicated data, so half is original
    const originalDataLength = sectionType === 'team' ? teamData.team.length : teamData.advisors.length;
    const singleSetWidth = originalDataLength * (cardWidth + gap);
    
    let newPosition;
    
    if (direction === 'left') {
      newPosition = currentScroll - scrollAmount;
      
      // If we're scrolling past the beginning, jump to the equivalent position in the second set
      if (newPosition < 0) {
        newPosition = singleSetWidth + newPosition;
        section.scrollLeft = newPosition;
      } else {
        await smoothScrollTo(section, newPosition);
      }
    } else {
      newPosition = currentScroll + scrollAmount;
      
      // If we're scrolling past the first set, check if we need to loop
      if (newPosition >= singleSetWidth) {
        // If we're in the second set and going past it, loop back to beginning
        if (currentScroll >= singleSetWidth) {
          newPosition = newPosition - singleSetWidth;
          section.scrollLeft = newPosition;
        } else {
          // Normal scroll into the second set
          await smoothScrollTo(section, newPosition);
        }
      } else {
        // Normal scroll within the first set
        await smoothScrollTo(section, newPosition);
      }
    }
    
    console.log(`Scrolling ${sectionType} ${direction}: ${currentScroll} -> ${newPosition} (singleSetWidth: ${singleSetWidth})`);
    
    // Update state
    if (sectionType === 'team') {
      setTeamScrollPosition(newPosition);
    } else {
      setAdvisorScrollPosition(newPosition);
    }
  };

  const PartnerSection = () => {
    const containerRef = useRef(null);
    const trackRef = useRef(null);
    const animationRef = useRef(null);
    const [isPaused, setIsPaused] = useState(false);
    const scrollPositionRef = useRef(0);

    React.useEffect(() => {
      const container = containerRef.current;
      const track = trackRef.current;
      if (!container || !track) return;

      const scrollSpeed = 1; // pixels per frame
      
      const animate = () => {
        if (!isPaused) {
          scrollPositionRef.current += scrollSpeed;
          
          // Get the width of a single partner slide (including gap)
          const firstSlide = track.querySelector('.partner-slide');
          if (firstSlide) {
            const slideWidth = firstSlide.offsetWidth + 48; // 96px = 6rem gap
            
            // When we've scrolled past one complete slide, move the first slide to the end
            if (scrollPositionRef.current >= slideWidth) {
              scrollPositionRef.current = 0;
              
              // Move the first slide to the end
              const firstChild = track.firstElementChild;
              if (firstChild) {
                track.appendChild(firstChild);
              }
            }
          }
          
          track.style.transform = `translateX(-${scrollPositionRef.current}px)`;
        }
        
        animationRef.current = requestAnimationFrame(animate);
      };
      
      animationRef.current = requestAnimationFrame(animate);
      
      return () => {
        if (animationRef.current) {
          cancelAnimationFrame(animationRef.current);
        }
      };
    }, [isPaused]);

    return (
      <div className="team-section">
        <h2 className="section-title">Partners</h2>
        <div 
          ref={containerRef}
          className="partner-carousel-no-reset"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          <div 
            ref={trackRef}
            className="partner-track-no-reset"
          >
            {/* Create enough initial slides for smooth operation */}
            {[...partnerData, ...partnerData, ...partnerData].map((partner, index) => (
              <div key={`partner-${index}`} className="partner-slide">
                <img 
                  src={partner.logo} 
                  alt={partner.name}
                  className="partner-logo"
                  style={{
                    width: partner.width,
                    height: partner.height
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // Cleanup animation on unmount
  React.useEffect(() => {
    return () => {
      if (scrollingRef.current) {
        cancelAnimationFrame(scrollingRef.current);
      }
    };
  }, []);

  return (
    <div className="team-carousel">
      <TeamSection 
        title="Leadership" 
        data={teamData.team} 
        sectionType="team" 
        onScroll={scrollSection}
        isScrolling={isScrolling}
      />
      <TeamSection 
        title="Advisors" 
        data={teamData.advisors} 
        sectionType="advisors" 
        onScroll={scrollSection}
        isScrolling={isScrolling}
      />
      <PartnerSection />
    </div>
  );
};

export default TeamCarousel;